import { h } from 'preact';
import RatingForm, { RatingAttribute } from './rating-from/rating-form';
import { get, map, filter, isEmpty as _isEmpty } from 'lodash';
import { useStore } from '../../helpers/use-store';
import { useCallback, useState } from 'preact/hooks';
import Spinner from '../../components/spinner/spinner';
import ThankYou from '../../components/thank-you/thank-you';
import ProofOfDelivery from './proof-of-delivery/proof-of-delivery';
import { TaskNote } from '@bringg/types';

export enum PageState {
	Loading,
	ProofOfDelivery,
	Rating,
	Done
}

const getInitialStateByConfig = (shareCustomerNotes: TaskNote[]) => {
	if (_isEmpty(shareCustomerNotes)) {
		return PageState.Rating;
	}

	return PageState.ProofOfDelivery;
};

const RatingPage = () => {
	const {
		sharedLocationStore: { sharedLocationConfig, getActiveTrackingStore }
	} = useStore();
	const { shareCustomerNotes } = getActiveTrackingStore();
	const [pageState, setPageState] = useState<PageState>(getInitialStateByConfig(shareCustomerNotes));

	const { sharedLocationStore } = useStore();

	const setStateToRating = useCallback(() => {
		setPageState(PageState.Rating);
	}, []);

	const onRate = async ({ rating, attributes, comment }) => {
		const reasonIds = getReasonIds(attributes);
		setPageState(PageState.Loading);
		await sharedLocationStore.rate(rating, reasonIds, comment);
		setPageState(PageState.Done);
	};

	const onSkip = () => {
		setPageState(PageState.Done);
	};

	const { rating_reason, rate_us_title_string, isUserAuthenticated, is_green_delivery } = sharedLocationConfig;

	const goodRatingThreshold = get(rating_reason, 'rating');
	const ratingNegativeTitle = get(rating_reason, 'title');
	const ratingPositiveTitle = get(rating_reason, 'positive_title');
	const allowFreeTextComment = get(rating_reason, 'allow_free_text_comment');

	const attributeList = getRateReasons(sharedLocationStore.sharedLocationConfig);

	return (
		<div className="rating-page" data-testid="rating-page">
			{pageState === PageState.Loading && <Spinner applyTheme float />}
			{pageState === PageState.ProofOfDelivery && (
				<ProofOfDelivery customerNotes={shareCustomerNotes} onRateUs={setStateToRating} />
			)}
			{PageState.Rating === pageState && (
				<RatingForm
					onSkip={onSkip}
					onSubmit={onRate}
					allowFreeTextComment={allowFreeTextComment}
					goodRatingThreshold={goodRatingThreshold}
					attributeList={attributeList}
					rateUsCustomTitle={rate_us_title_string}
					ratingNegativeTitle={ratingNegativeTitle}
					ratingPositiveTitle={ratingPositiveTitle}
					className="rating-page-form"
					isUserAuthenticated={isUserAuthenticated}
					showGreenDeliveryTitle={is_green_delivery}
				/>
			)}
			{pageState === PageState.Done && <ThankYou />}
		</div>
	);
};

export const getRateReasons = sharedLocation => {
	const items = get(sharedLocation, 'rating_reason.items');
	return map(items, ({ id, text }) => ({ id, value: text }));
};

export const getReasonIds = (attributes: Map<number, RatingAttribute>) => {
	const keys = Array.from(attributes.keys());
	return filter(keys, id => attributes.get(id).isChecked);
};

export default RatingPage;
